/**
 * 11.3 — entrar no Portal, e aceitar o convite (lote P2).
 *
 * ## O que mudou, e por quê
 *
 * **O convite trava o e-mail.** O cartão de convite não pedia e-mail nenhum: o token
 * carrega a identidade, e pedir de novo abriria a porta para a pessoa digitar outro
 * endereço e ficar sem entender por que "não funcionou". Agora ele MOSTRA o e-mail
 * (desabilitado) — a pessoa confere para quem é o acesso antes de criar a senha.
 *
 * **Confirmar a senha.** Definir uma senha que só existe uma vez, num campo mascarado,
 * é a receita para o primeiro acesso virar um pedido de recuperação. São dois campos, e
 * a comparação acontece antes de sair da tela.
 *
 * **Mínimo de 8 no cliente.** A política é do servidor e continua sendo; a tela cobra o
 * mesmo mínimo para a recusa não custar uma viagem.
 */
import { useState } from 'react'
import { ArrowRight, Eye, EyeOff } from 'lucide-react'
import { DIAS_DE_DISPOSITIVO_CONFIAVEL, euExternoDoPortal, type EuExternoDoPortal } from '@dv/contracts'
import { enviar } from '../api/cliente.js'
import { FalhaDoPortal } from '../api/erros.js'

/** O convite vem na URL; o e-mail dele vem no próprio token, lido pelo servidor. */
function conviteDaUrl(): string | null {
  return new URLSearchParams(location.search).get('convite')
}

export function Login({
  aoEntrar,
  emailDoConvite,
  aoEsquecerSenha,
}: {
  aoEntrar: (eu: EuExternoDoPortal) => void
  /** Quando o convite é válido, o servidor já disse de quem ele é. */
  emailDoConvite: string | null
  /** Leva ao cartão de recuperação — mesma moldura, sem tela nova (spec 11.3). */
  aoEsquecerSenha: () => void
}) {
  const convite = conviteDaUrl()
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [confirmacao, setConfirmacao] = useState('')
  const [mostrarSenha, setMostrarSenha] = useState(false)
  const [manterConectado, setManterConectado] = useState(false)
  const [erro, setErro] = useState('')
  const [ocupado, setOcupado] = useState(false)

  const aceitando = convite !== null

  async function submeter(evento: React.FormEvent): Promise<void> {
    evento.preventDefault()
    setErro('')

    if (aceitando) {
      if (senha.length < 8) {
        setErro('A senha precisa de ao menos 8 caracteres.')
        return
      }
      if (senha !== confirmacao) {
        setErro('As duas senhas não são iguais.')
        return
      }
    }

    setOcupado(true)
    try {
      const eu = aceitando
        ? await enviar('auth/aceitar-convite', { token: convite, senha }, euExternoDoPortal)
        : await enviar(
            'auth/login',
            { email, senha, manter_conectado: manterConectado },
            euExternoDoPortal,
          )
      aoEntrar(eu)
    } catch (falha) {
      setErro(
        falha instanceof FalhaDoPortal && falha.tipo !== 'contrato'
          ? falha.message
          : 'Não foi possível entrar.',
      )
    } finally {
      setOcupado(false)
    }
  }

  return (
    <main className="entrada">
      <div className="entrada-conteudo">
        {/*
          A MARCA, e não a palavra escrita à mão.

          Até 31/08 esta linha era `<div class="marca-login">digitalview</div>`: texto
          puro, fiel ao protótipo 11.3, que também nunca teve o logo. O app interno
          usava o PNG desde sempre — a fronteira do defeito era exatamente a fronteira
          entre os dois protótipos.

          Os dois únicos logos do repositório são BRANCOS, para o navy da sidebar, e
          aqui o fundo é claro. O arquivo abaixo é o mesmo desenho recolorido para o
          navy da marca (`#0E2A5C`), gerado do original sem redesenhar traço nenhum.
        */}
        <img className="marca-login" src="/marca/logo-digitalview-navy.png" alt="DigitalView" width={168} height={33} />
        {/*
          Ele SEMPRE foi o título do bloco; só não dizia isso na marcação.

          No protótipo (11.3:45) esta linha é `<p class="eyebrow">Portal do Cliente</p>`,
          irmã do "ACESSO EXTERNO" do cartão: mesma família, mesma tipografia. Aqui ela
          nascera com classe própria e caixa de sentença, e a diferença não era só de
          etiqueta: 12px sem espaçamento de letra contra 11px com .09em. Declarada como
          eyebrow, a tipografia do protótipo passa a valer, e a caixa alta vem no literal,
          como no irmão dela. O que continua sendo só desta linha, por ela morar FORA do
          cartão, é a centralização e o respiro até ele, guardados no `.portal-tipo`.
        */}
        {/*
          "Portal de clientes e parceiros", e não "Portal do Cliente" (PK-06A · DS-519): o login é o
          mesmo para as duas visões, e o selo do protótipo contradizia a frase logo abaixo.
        */}
        <p className="portal-tipo eyebrow">PORTAL DE CLIENTES E PARCEIROS</p>
        <section className="login-card">
          <p className="eyebrow">ACESSO EXTERNO</p>
          <h1>{aceitando ? 'Definir sua senha' : 'Acessar o portal'}</h1>
          {/* Sem a frase "Área exclusiva de clientes e parceiros": ela repetia o selo logo acima do cartão (D-203). */}
          {erro !== '' && (
            <div className="erro" role="alert">
              {erro}
            </div>
          )}
          <form
            onSubmit={(evento) => {
              void submeter(evento)
            }}
          >
            {aceitando ? (
              emailDoConvite !== null && (
                <label htmlFor="portal-email">
                  E-mail
                  {/*
                    Travado: a identidade é do TOKEN. O campo existe para a pessoa
                    conferir para quem é o acesso, não para escolher.
                  */}
                  <input id="portal-email" type="email" value={emailDoConvite} disabled readOnly />
                </label>
              )
            ) : (
              <label htmlFor="portal-email">
                E-mail
                <input
                  id="portal-email"
                  type="email"
                  placeholder="voce@empresa.com"
                  value={email}
                  onChange={(evento) => {
                    setEmail(evento.target.value)
                  }}
                  required
                  autoComplete="email"
                />
              </label>
            )}

            <div className="grupo-campo">
              <span className="rotulo-senha">
                <label htmlFor="portal-senha">Senha</label>
                {/*
                  Vivo desde a migration 0093.

                  Ele passou uma execução desabilitado, e o motivo era real: a API
                  externa do Portal não alcança a fila, e alargar o privilégio do papel
                  de banco para enfileirar seria dar a ele o poder de enfileirar
                  QUALQUER coisa — inclusive os jobs financeiros internos (DS-326).
                  A saída não foi alargar o privilégio: foi a tabela fechada com função
                  de propósito único, que dá exatamente esta porta e nenhuma outra.
                */}
                {!aceitando && (
                  <button
                    className="link"
                    type="button"
                    onClick={aoEsquecerSenha}
                    data-teste="esqueci-senha"
                  >
                    Esqueci a senha
                  </button>
                )}
              </span>
              <span className="campo-senha">
                <input
                  id="portal-senha"
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  minLength={aceitando ? 8 : 1}
                  value={senha}
                  onChange={(evento) => {
                    setSenha(evento.target.value)
                  }}
                  required
                  autoComplete={aceitando ? 'new-password' : 'current-password'}
                />
                <button
                  type="button"
                  onClick={() => {
                    setMostrarSenha((valor) => !valor)
                  }}
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {mostrarSenha ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </span>
              {aceitando && <small className="ajuda-senha">Ao menos 8 caracteres.</small>}
            </div>

            {aceitando && (
              <label htmlFor="portal-confirmacao">
                Confirmar senha
                <input
                  id="portal-confirmacao"
                  type={mostrarSenha ? 'text' : 'password'}
                  placeholder="••••••••"
                  minLength={8}
                  value={confirmacao}
                  onChange={(evento) => {
                    setConfirmacao(evento.target.value)
                  }}
                  required
                  autoComplete="new-password"
                />
              </label>
            )}

            {!aceitando && (
              <label className="manter">
                <input
                  type="checkbox"
                  checked={manterConectado}
                  onChange={(evento) => {
                    setManterConectado(evento.target.checked)
                  }}
                />{' '}
                Confiar neste dispositivo por {DIAS_DE_DISPOSITIVO_CONFIAVEL} dias
              </label>
            )}

            <button className="primario" disabled={ocupado}>
              {ocupado ? 'Verificando…' : aceitando ? 'Criar acesso e entrar' : 'Entrar'}
              <ArrowRight size={16} />
            </button>
          </form>
          {/*
            O alcance do acesso continua à vista ANTES de entrar (D-203: segurança e acesso), com a mesma frase do rodapé
            do portal, que só existe depois do login.
          */}
          <p className="nota">Acesso pessoal: você vê apenas os dados das empresas que representa.</p>
        </section>
        <p className="apoio">Não tem acesso? Fale com seu contato na DigitalView.</p>
      </div>
    </main>
  )
}
